import React, { useState } from 'react';
import { X, ShieldCheck } from 'lucide-react';

// Obfuscated email parts
const emailParts = ['luxeme', 'japan', '@', 'gmail', '.com'];
const getEmail = () => emailParts[0] + emailParts[1] + emailParts[2] + emailParts[3] + emailParts[4];

// Verification challenge — Flowers (Sakura = correct)
const challenge = {
  question: "Which Japanese flower blooms in spring and symbolises life's beauty?",
  options: [
    { id: 1, label: "Sakura",  image: "🌸", correct: true  },
    { id: 2, label: "Lotus",   image: "🪷", correct: false },
    { id: 3, label: "Orchid",  image: "🌺", correct: false },
  ]
};

// Hook for email verification
export const useEmailVerification = () => {
  const [showVerification, setShowVerification] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [verificationError, setVerificationError] = useState(false);

  const handleEmailClick = () => {
    if (isVerified) {
      sendEmail();
    } else {
      setShowVerification(true);
      setSelectedAnswer(null);
      setVerificationError(false);
    }
  };

  const handleVerify = () => {
    if (!selectedAnswer) return;
    
    const selected = challenge.options.find(opt => opt.id === selectedAnswer);
    if (selected && selected.correct) {
      setIsVerified(true);
      setShowVerification(false);
      setTimeout(() => {
        sendEmail();
      }, 300);
    } else {
      setVerificationError(true);
      setSelectedAnswer(null);
    }
  };

  const sendEmail = () => {
    const email = getEmail();
    const subject = encodeURIComponent("Inquiry - Luxe Me Japan");
    const body = encodeURIComponent(`Dear Luxe Me Japan Team,

I would like to inquire about your services.

Name: 
Company/Organization: 
Inquiry Type: [Partnership / Brand Representation / Event / Other]

Message:


Best regards`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const closeModal = () => {
    setShowVerification(false);
    setSelectedAnswer(null);
    setVerificationError(false);
  };

  return {
    showVerification,
    isVerified,
    selectedAnswer,
    verificationError,
    setSelectedAnswer,
    setVerificationError,
    handleEmailClick,
    handleVerify,
    closeModal
  };
};

// Verification Modal Component
export const EmailVerificationModal = ({ 
  showVerification, 
  selectedAnswer, 
  verificationError, 
  setSelectedAnswer, 
  setVerificationError,
  handleVerify, 
  closeModal 
}) => {
  if (!showVerification) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-onyx/95 backdrop-blur-xl flex items-center justify-center p-4"
      onClick={closeModal}
    >
      <div 
        className="bg-ebony rounded-3xl max-w-md w-full border border-gold/20 shadow-2xl shadow-gold/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-gold/10 to-transparent p-6 border-b border-gold/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-gold" />
              <h3 className="font-serif text-xl text-platinum">Verification</h3>
            </div>
            <button 
              onClick={closeModal}
              className="text-platinum/50 hover:text-gold transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8">
          <p className="text-platinum/70 text-center mb-6">
            Please verify you are human to access our contact.
          </p>

          {/* Question */}
          <p className="text-gold text-center font-medium mb-6">
            {challenge.question}
          </p>

          {/* Options */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {challenge.options.map((option) => (
              <button
                key={option.id}
                onClick={() => {
                  setSelectedAnswer(option.id);
                  setVerificationError(false);
                }}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-300 ${
                  selectedAnswer === option.id
                    ? 'border-gold bg-gold/10 scale-105'
                    : 'border-white/10 hover:border-gold/50'
                }`}
              >
                <span className="text-4xl">{option.image}</span>
                <span className="text-xs text-platinum/70">{option.label}</span>
              </button>
            ))}
          </div>

          {/* Error Message */}
          {verificationError && (
            <p className="text-red-400 text-sm text-center mb-4">
              Incorrect answer. Please try again.
            </p>
          )}

          {/* Verify Button */}
          <button
            onClick={handleVerify}
            disabled={!selectedAnswer}
            className={`w-full py-4 rounded-full font-bold tracking-widest uppercase text-xs transition-all duration-500 ${
              selectedAnswer
                ? 'bg-gold text-onyx hover:bg-white'
                : 'bg-platinum/20 text-platinum/40 cursor-not-allowed'
            }`}
          >
            Verify & Send Email
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationModal;
